import { useState, useEffect, useMemo } from 'react'
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Input,
  Modal,
  Form,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tag,
  Upload,
  Divider,
  List,
  Drawer,
  FloatButton,
  Badge,
  Grid,
  DatePicker,
  Image,
  Tooltip,
  Progress
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
  DollarOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  DownloadOutlined,
  FilterOutlined,
  FileTextOutlined,
  FileOutlined,
  CalendarOutlined,
  ShopOutlined,
  MoneyCollectOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  InboxOutlined,
  PrinterOutlined
} from '@ant-design/icons'
import { downloadExpensesTablePDF } from '../../services/expenseTablePdfService'
import dayjs from 'dayjs'
import expenseService from '../../services/expenseService'
import contactService from '../../services/contactService'
import { useProjectsOptions } from '../../hooks/useProjects'
import FileViewer from '../../components/FileViewer'
import { useAuth } from '../../contexts/AuthContext'
import { generateExpensePDF } from '../../services/expensePdfService'
import { getCompanySettings, getLogoUrl } from '../../services/settingsService'
import { getContactById } from '../../services/contactService'
import { buildAssetUrl } from '../../config/runtimeConfig'
// import { PrinterOutlined } from '@ant-design/icons'

// Utility function to convert relative file URL to absolute URL
const getFileUrl = (relativeUrl) => {
  if (!relativeUrl) return null
  if (relativeUrl.startsWith('http')) return relativeUrl
  return buildAssetUrl(relativeUrl)
}

const { Title, Text } = Typography
const { Search } = Input
const { Option } = Select
const { RangePicker } = DatePicker
const { Dragger } = Upload
const { useBreakpoint } = Grid

const ExpensesPage = () => {

  const { user } = useAuth()
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [editingExpense, setEditingExpense] = useState(null)
  const [viewingExpense, setViewingExpense] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedSupplier, setSelectedSupplier] = useState('all')
  const [selectedProject, setSelectedProject] = useState('all')
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [form] = Form.useForm()
  const [suppliers, setSuppliers] = useState([])
  const [categories, setCategories] = useState([])
  const [defaultCategories, setDefaultCategories] = useState([])
  // Calculate stats from expenses array directly
  const stats = useMemo(() => {
    const total_amount = expenses.reduce((sum, expense) => sum + parseFloat(expense.total_amount || 0), 0)
    const approved_amount = expenses
      .filter(e => e.status === 'approved' || e.status === 'paid')
      .reduce((sum, expense) => sum + parseFloat(expense.total_amount || 0), 0)
    const pending_amount = expenses
      .filter(e => e.status === 'pending')
      .reduce((sum, expense) => sum + parseFloat(expense.total_amount || 0), 0)
    const total_expenses = expenses.length

    return {
      total_amount,
      approved_amount,
      pending_amount,
      total_expenses
    }
  }, [expenses])
  const [fileList, setFileList] = useState([])
  const [uploading, setUploading] = useState(false)

  // PDF Modal state
  const [pdfModalVisible, setPdfModalVisible] = useState(false)
  const [pdfDataUrl, setPdfDataUrl] = useState('')
  const [company, setCompany] = useState(null)

  // VAT modes
  const vatModeOptions = [
    { value: 'no_tax', label: 'ไม่มีภาษี', color: 'green' },
    { value: 'exclude', label: 'แยกภาษี (VAT EXCLUDE)', color: 'blue' },
    { value: 'include', label: 'รวมภาษี (VAT INCLUDE)', color: 'green' }
  ];

  const calculateTotals = (items, discount = 0, taxRate = 7, vatMode = 'exclude') => {
    const totalBeforeDiscount = (items || []).reduce((sum, item) => {
      if (!item) return sum;
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + qty * price;
    }, 0);

    const discountAmount = parseFloat(discount) || 0;

    let subtotal = 0;
    let taxAmount = 0;
    let totalAmount = 0;

    if (vatMode === 'include') {
      totalAmount = totalBeforeDiscount - discountAmount;
      subtotal = totalAmount / (1 + taxRate / 100);
      taxAmount = totalAmount - subtotal;
    } else if (vatMode === 'exclude') {
      subtotal = totalBeforeDiscount - discountAmount;
      taxAmount = (subtotal * taxRate) / 100;
      totalAmount = subtotal + taxAmount;
    } else {
      subtotal = totalBeforeDiscount - discountAmount;
      taxAmount = 0;
      totalAmount = subtotal;
    }

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      discount: parseFloat(discountAmount.toFixed(2)),
      subtotalAfterDiscount: parseFloat((subtotal).toFixed(2)),
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2))
    };
  };

  // Date filter states
  const [selectedDateRange, setSelectedDateRange] = useState([null, null])
  const [selectedQuickFilter, setSelectedQuickFilter] = useState('all')
  const [selectedYear, setSelectedYear] = useState(dayjs().year())

  const screens = useBreakpoint()
  const isMobile = !screens.md

  // Payment methods
  const paymentMethods = [
    { value: 'cash', label: 'เงินสด' },
    { value: 'transfer', label: 'โอนเงิน' },
    { value: 'credit_card', label: 'บัตรเครดิต' },
    { value: 'debit_card', label: 'บัตรเดบิต' },
    { value: 'check', label: 'เช็ค' },
    { value: 'other', label: 'อื่นๆ' }
  ]

  // Project options hook
  const { projectOptions, loading: projectsLoading } = useProjectsOptions({
    status: 'active',
    includeCode: true
  })

  // Month options
  const monthOptions = [
    { value: 0, label: 'ม.ค.' },
    { value: 1, label: 'ก.พ.' },
    { value: 2, label: 'มี.ค.' },
    { value: 3, label: 'เม.ย.' },
    { value: 4, label: 'พ.ค.' },
    { value: 5, label: 'มิ.ย.' },
    { value: 6, label: 'ก.ค.' },
    { value: 7, label: 'ส.ค.' },
    { value: 8, label: 'ก.ย.' },
    { value: 9, label: 'ต.ค.' },
    { value: 10, label: 'พ.ย.' },
    { value: 11, label: 'ธ.ค.' }
  ]

  // Year options (current year +/- 5 years)
  const yearOptions = useMemo(() => {
    const currentYear = dayjs().year()
    const years = []
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push({ value: i, label: (i + 543).toString() }) // Display in Buddhist year
    }
    return years
  }, [])

  useEffect(() => {
    fetchExpenses({}) // รีเฟรชครั้งแรก
    fetchSuppliers()
    fetchCategories()
    fetchSettings()
  }, [])

  // *** REMOVED: useEffect dependency เพื่อป้องกัน timing issue และ duplicate calls ***
  // แต่ละ filter handler จะจัดการ fetch เอง

  const fetchExpenses = async (customParams = {}) => {
    setLoading(true)
    try {
      // Prepare date parameters
      let startDate, endDate
      const dateRange = customParams.dateRange || selectedDateRange

      if (dateRange && dateRange[0] && dateRange[1]) {
        // Ensure they are dayjs objects before formatting
        startDate = dayjs.isDayjs(dateRange[0]) ? dateRange[0].format('YYYY-MM-DD') : dateRange[0]
        endDate = dayjs.isDayjs(dateRange[1]) ? dateRange[1].format('YYYY-MM-DD') : dateRange[1]
      }

      // Prepare filter parameters - ใช้ค่าใหม่หรือค่าเก่า
      const searchParam = customParams.search !== undefined ? customParams.search : searchTerm
      const categoryParam = customParams.category !== undefined ? customParams.category : selectedCategory
      const statusParam = customParams.status !== undefined ? customParams.status : selectedStatus
      const supplierParam = customParams.supplier !== undefined ? customParams.supplier : selectedSupplier
      const projectParam = customParams.project !== undefined ? customParams.project : selectedProject

      // สามารถเปิด debug ได้ตามต้องการ
      // console.log('FETCH EXPENSES:', { searchParam, categoryParam, statusParam, supplierParam, projectParam, startDate, endDate })

      const response = await expenseService.getAllExpenses({
        search: searchParam,
        category: categoryParam === 'all' ? '' : categoryParam,
        status: statusParam === 'all' ? '' : statusParam,
        supplier_id: supplierParam === 'all' ? '' : supplierParam,
        project_id: projectParam === 'all' ? '' : projectParam,
        start_date: startDate,
        end_date: endDate,
        limit: 50
      })
      setExpenses(response.data)
    } catch (error) {
      //message.error('ไม่สามารถโหลดข้อมูลค่าใช้จ่ายได้')
      console.error('Error fetching expenses:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSuppliers = async () => {
    try {
      const response = await contactService.getAllContacts({ limit: 10000, type: '' })
      setSuppliers(response.data || [])
    } catch (error) {
      //console.error('Error fetching suppliers:', error)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await expenseService.getCategories()
      setCategories(response.data || [])
      setDefaultCategories(response.defaultCategories || [])
    } catch (error) {
      //console.error('Error fetching categories:', error)
    }
  }



  const fetchSettings = async () => {
    try {
      const response = await getCompanySettings()
      if (response.data.success) {
        setCompany(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching settings:', error)
    }
  }

  const handleAddExpense = () => {
    setEditingExpense(null)
    setModalVisible(true)
    setFileList([])
    form.resetFields()
    form.setFieldsValue({
      expense_date: dayjs(),
      status: 'pending',
      tax_amount: 0,
      items: [{ description: '', quantity: 1, unit_price: 0 }]
    })
  }

  const handleEditExpense = async (expense) => {
    try {
      const fullExpense = await expenseService.getExpenseById(expense.id)
      setEditingExpense(fullExpense)
      setModalVisible(true)
      setFileList([])

      form.setFieldsValue({
        expense_number: fullExpense.expense_number,
        expense_date: dayjs(fullExpense.expense_date),
        category: fullExpense.category,
        description: fullExpense.description,
        amount: fullExpense.amount,
        tax_amount: fullExpense.tax_amount || 0,
        payment_method: fullExpense.payment_method,
        supplier_id: fullExpense.supplier_id,
        project_id: fullExpense.project_id,
        notes: fullExpense.notes,
        status: fullExpense.status,
        vat_mode: fullExpense.vat_mode || 'exclude',
        tax_rate: fullExpense.tax_rate || 7,
        discount: fullExpense.discount || 0,
        items: fullExpense.items?.length > 0 ? fullExpense.items : [{ description: '', quantity: 1, unit_price: 0 }]
      })
    } catch (error) {
      //message.error('ไม่สามารถโหลดข้อมูลค่าใช้จ่ายได้')
    }
  }

  const handleViewExpense = async (expense) => {
    try {
      const fullExpense = await expenseService.getExpenseById(expense.id)
      setViewingExpense(fullExpense)
      setDetailVisible(true)
    } catch (error) {
      //message.error('ไม่สามารถโหลดข้อมูลค่าใช้จ่ายได้')
    }
  }

  const handleDeleteExpense = async (id) => {
    try {
      await expenseService.deleteExpense(id)
      message.success('ลบค่าใช้จ่ายสำเร็จ')
      fetchExpenses({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
    } catch (error) {
      message.error('ไม่สามารถลบค่าใช้จ่ายได้')
    }
  }

  const handleApproveExpense = async (id) => {
    try {
      await expenseService.approveExpense(id)
      message.success('อนุมัติค่าใช้จ่ายสำเร็จ')
      fetchExpenses({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
    } catch (error) {
      message.error('ไม่สามารถอนุมัติค่าใช้จ่ายได้')
    }
  }

  const handlePrint = async (expense) => {
    try {
      message.loading('กำลังสร้าง PDF...', 1)

      // โหลดข้อมูล expense แบบเต็ม
      const fullExpense = await expenseService.getExpenseById(expense.id)

      // ถ้ามี supplier_id ให้โหลดข้อมูล supplier เพิ่มเติม
      let supplierData = null
      if (fullExpense.supplier_id) {
        try {
          supplierData = await getContactById(fullExpense.supplier_id)
        } catch (err) {
          console.error('Error fetching supplier details:', err)
        }
      }

      // สร้าง PDF
      const dataUrl = await generateExpensePDF(fullExpense, company, supplierData)
      setPdfDataUrl(dataUrl)
      setPdfModalVisible(true)
      message.success('สร้าง PDF เรียบร้อยแล้ว')
    } catch (error) {
      console.error('Error generating PDF:', error)
      message.error('เกิดข้อผิดพลาดในการสร้าง PDF')
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()

      // Upload receipt image if any (first file only based on API structure)
      let receiptImageUrl = editingExpense?.receipt_image || null
      if (fileList.length > 0) {
        setUploading(true)
        const filesToUpload = fileList.filter(file => !file.url)
        if (filesToUpload.length > 0) {
          // Upload only the first file as receipt_image (based on API structure)
          const uploadResponse = await expenseService.uploadFile(
            filesToUpload[0].originFileObj || filesToUpload[0],
            'expenses'
          )
          receiptImageUrl = uploadResponse.url || uploadResponse.file?.url || null
        }
      }

      // Calculate totals
      const items = values.items || [];
      const discount = values.discount || 0;
      const taxRate = values.tax_rate || 0;
      const vatMode = values.vat_mode || 'exclude';

      const calcValues = calculateTotals(items, discount, taxRate, vatMode);

      const expenseData = {
        ...values,
        expense_date: values.expense_date.format('YYYY-MM-DD'),
        amount: calcValues.subtotal, // Base amount before tax
        subtotal: calcValues.subtotal,
        discount: calcValues.discount,
        tax_amount: calcValues.taxAmount,
        total_amount: calcValues.totalAmount,
        vat_mode: vatMode,
        tax_rate: taxRate,
        receipt_image: receiptImageUrl,
        items: values.items?.filter(item => item.description && item.unit_price) || []
      }

      if (editingExpense) {
        await expenseService.updateExpense(editingExpense.id, expenseData)
        message.success('อัปเดตค่าใช้จ่ายสำเร็จ')
      } else {
        await expenseService.createExpense(expenseData)
        message.success('เพิ่มค่าใช้จ่ายสำเร็จ')
      }

      setModalVisible(false)
      setUploading(false)
      form.resetFields()
      setFileList([])
      fetchExpenses({}) // รีเฟรชข้อมูลด้วย filter ปัจจุบัน
    } catch (error) {
      setUploading(false)
      if (error.errors) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วน')
      } else {
        message.error(error.message || 'ไม่สามารถบันทึกข้อมูลได้')
      }
    }
  }

  const handleModalCancel = () => {
    setModalVisible(false)
    setDetailVisible(false)
    form.resetFields()
    setFileList([])
    setUploading(false)
  }

  // *** FIXED: แก้ไขปัญหา timing issue สำหรับทุก Filter ***
  const handleSearch = (value) => {
    setSearchTerm(value)
    setTimeout(() => fetchExpenses({ search: value }), 300)
  }

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category)
    setTimeout(() => fetchExpenses({ category: category }), 300)
  }

  const handleStatusFilter = (status) => {
    setSelectedStatus(status)
    setTimeout(() => fetchExpenses({ status: status }), 300)
  }

  const handleSupplierFilter = (supplier) => {
    setSelectedSupplier(supplier)
    setTimeout(() => fetchExpenses({ supplier: supplier }), 300)
  }

  const handleProjectFilter = (project) => {
    setSelectedProject(project)
    setTimeout(() => fetchExpenses({ project: project }), 300)
  }

  // Date filter handlers
  const handleQuickDateFilter = (filterType) => {
    setSelectedQuickFilter(filterType)
    const today = dayjs()
    let startDate, endDate

    // สามารถเปิด debug ได้ตามต้องการ
    // console.log('Date Filter:', filterType, 'Today:', today.format('YYYY-MM-DD'))

    switch (filterType) {
      case 'this_month':
        startDate = today.clone().startOf('month')
        endDate = today.clone().endOf('month')
        // console.log('THIS MONTH:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'))
        break
      case 'last_month':
        startDate = today.clone().subtract(1, 'month').startOf('month')
        endDate = today.clone().subtract(1, 'month').endOf('month')
        // console.log('LAST MONTH:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'))
        break
      case 'last_3_months':
        startDate = today.clone().subtract(3, 'months').startOf('month')
        endDate = today.clone().endOf('month')
        break
      case 'last_6_months':
        startDate = today.clone().subtract(6, 'months').startOf('month')
        endDate = today.clone().endOf('month')
        break
      case 'this_year':
        startDate = today.clone().startOf('year')
        endDate = today.clone().endOf('year')
        break
      case 'last_year':
        startDate = today.clone().subtract(1, 'year').startOf('year')
        endDate = today.clone().subtract(1, 'year').endOf('year')
        break
      default:
        startDate = null
        endDate = null
    }

    // console.log('Date Filter Result:', startDate?.format('YYYY-MM-DD'), 'to', endDate?.format('YYYY-MM-DD'))

    setSelectedDateRange([startDate, endDate])
    setTimeout(() => fetchExpenses({ dateRange: [startDate, endDate] }), 300)
  }

  const handleDateRangeChange = (dates) => {
    const dateRange = dates || [null, null]
    setSelectedDateRange(dateRange)
    setSelectedQuickFilter('custom')
    setTimeout(() => fetchExpenses({ dateRange: dateRange }), 300)
  }

  const handleMonthFilter = (monthValue) => {
    if (monthValue === 'all') {
      clearDateFilter()
      return
    }
    const startDate = dayjs().year(selectedYear).month(monthValue).startOf('month')
    const endDate = dayjs().year(selectedYear).month(monthValue).endOf('month')

    setSelectedQuickFilter('month_select')
    setSelectedDateRange([startDate, endDate])
    setTimeout(() => fetchExpenses({ dateRange: [startDate, endDate] }), 300)
  }

  const handleYearFilter = (yearValue) => {
    setSelectedYear(yearValue)

    // If a month is already selected, update the range for the new year
    if (selectedQuickFilter === 'month_select' && selectedDateRange[0]) {
      const currentMonth = selectedDateRange[0].month()
      const startDate = dayjs().year(yearValue).month(currentMonth).startOf('month')
      const endDate = dayjs().year(yearValue).month(currentMonth).endOf('month')
      setSelectedDateRange([startDate, endDate])
      setTimeout(() => fetchExpenses({ dateRange: [startDate, endDate] }), 300)
    } else if (selectedQuickFilter === 'this_year' || selectedQuickFilter === 'last_year' || selectedQuickFilter === 'all') {
      // If no specific month/range, just update the year state
      // (Optionally could trigger a filter for the whole year)
    }
  }

  const clearDateFilter = () => {
    const dateRange = [null, null]
    setSelectedDateRange(dateRange)
    setSelectedQuickFilter('all')
    setTimeout(() => fetchExpenses({ dateRange: dateRange }), 300)
  }

  // File upload properties (single receipt image based on API structure)
  const uploadProps = {
    multiple: false,
    maxCount: 1,
    fileList,
    beforeUpload: (file) => {
      // Replace existing file if any (since API supports only one receipt_image)
      setFileList([file])
      return false // Prevent auto upload
    },
    onRemove: (file) => {
      setFileList([])
    },
    accept: '.jpg,.jpeg,.png,.pdf',
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      paid: 'blue',
      rejected: 'red'
    }
    return colors[status] || 'default'
  }

  const getStatusText = (status) => {
    const texts = {
      pending: 'รอดำเนินการ',
      approved: 'อนุมัติแล้ว',
      paid: 'จ่ายแล้ว',
      rejected: 'ไม่อนุมัติ'
    }
    return texts[status] || status
  }

  const columns = [
    {
      title: 'เลขที่',
      dataIndex: 'expense_number',
      key: 'expense_number',
      width: 130,
      render: (number) => <Tag color="blue">{number}</Tag>
    },
    {
      title: 'วันที่',
      dataIndex: 'expense_date',
      key: 'expense_date',
      width: 110,
      render: (date) => dayjs(date).format('DD/MM/YYYY')
    },
    {
      title: 'หมวดหมู่',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => <Tag color="green">{category}</Tag>
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: true
    },
    {
      title: 'ผู้จำหน่าย',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 200,
      render: (name, record) => name || record.supplier_company || '-'
    },
    {
      title: 'จำนวนเงิน',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      render: (amount) => `₿${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    },
    {
      title: 'ภาษี',
      dataIndex: 'tax_amount',
      key: 'tax_amount',
      width: 100,
      render: (tax) => tax > 0 ? `₿${parseFloat(tax).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      )
    },
    {
      title: 'การจัดการ',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewExpense(record)}
            size="small"
            title="ดูรายละเอียด"
          />
          {record.status === 'pending' && user.role === 'SUPER_ADMIN' && (
            <Button
              type="link"
              icon={<CheckOutlined />}
              onClick={() => handleApproveExpense(record.id)}
              size="small"
              title="อนุมัติ"
              style={{ color: '#52c41a' }}
            />
          )}

          <Button
            type="link"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(record)}
            size="small"
            title="พิมพ์"
            style={{ color: '#7e57c2' }}
          />

          {user.role === 'SUPER_ADMIN' && (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditExpense(record)}
                size="small"
                title="แก้ไข"
              />
              <Popconfirm
                title="คุณแน่ใจว่าต้องการลบค่าใช้จ่ายนี้?"
                onConfirm={() => handleDeleteExpense(record.id)}
                okText="ใช่"
                cancelText="ไม่"
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  title="ลบ"
                />
              </Popconfirm>
            </>
          )}

        </Space>
      )
    }
  ]

  const handlePrintTable = async () => {
    try {
      setLoading(true)

      // Prepare filters object
      const filters = {
        search: searchTerm,
        category: selectedCategory !== 'all' ? selectedCategory : '',
        status: selectedStatus !== 'all' ? selectedStatus : '',
        supplier_id: selectedSupplier !== 'all' ? selectedSupplier : '',
        start_date: selectedDateRange[0] ? selectedDateRange[0].format('YYYY-MM-DD') : '',
        end_date: selectedDateRange[1] ? selectedDateRange[1].format('YYYY-MM-DD') : ''
      }

      await downloadExpensesTablePDF(expenses, company, filters, stats)
      message.success('สร้างไฟล์ PDF เรียบร้อยแล้ว')
    } catch (error) {
      console.error('Error printing table:', error)
      message.error('ไม่สามารถพิมพ์ตารางได้')
    } finally {
      setLoading(false)
    }
  }

  // Mobile Expense Card Component
  const ExpenseCard = ({ expense }) => (
    <Card
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewExpense(expense)}
          size="small"
        >
          ดู
        </Button>,
        expense.status === 'pending' && user.role === 'SUPER_ADMIN' && (
          <Button
            type="text"
            icon={<CheckOutlined />}
            onClick={() => handleApproveExpense(expense.id)}
            size="small"
            style={{ color: '#52c41a' }}
          >
            อนุมัติ
          </Button>
        ),
        <Button
          type="text"
          icon={<PrinterOutlined />}
          onClick={() => handlePrint(expense)}
          size="small"
          style={{ color: '#7e57c2' }}
        >
          พิมพ์
        </Button>,
        user.role === 'SUPER_ADMIN' && (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditExpense(expense)}
            size="small"
          >
            แก้ไข
          </Button>,
          <Popconfirm
            title="ลบค่าใช้จ่ายนี้?"
            onConfirm={() => handleDeleteExpense(expense.id)}
            okText="ใช่"
            cancelText="ไม่"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              ลบ
            </Button>
          </Popconfirm>
        )
      ].filter(Boolean)}
    >
      <Row gutter={12}>
        <Col span={24}>
          <div style={{ marginBottom: 8 }}>
            <Tag color="blue" style={{ marginBottom: 4 }}>{expense.expense_number}</Tag>
            <Tag color={getStatusColor(expense.status)}>{getStatusText(expense.status)}</Tag>
          </div>

          <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
            {expense.description}
          </Text>

          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary">วันที่:</Text><br />
              <Text>{dayjs(expense.expense_date).format('DD/MM/YYYY')}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">หมวดหมู่:</Text><br />
              <Tag color="green" size="small">{expense.category}</Tag>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text type="secondary">จำนวนเงิน:</Text><br />
              <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                ₿{parseFloat(expense.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">ภาษี:</Text><br />
              <Text style={{ fontSize: 16 }}>
                {expense.tax_amount > 0 ? `₿${parseFloat(expense.tax_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
              </Text>
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={24}>
              <Text type="secondary">ผู้จำหน่าย:</Text><br />
              <Text>{expense.supplier_name || expense.supplier_company || '-'}</Text>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  )

  // Render Filters Component
  const FiltersComponent = () => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Search
          placeholder="ค้นหาค่าใช้จ่าย..."
          allowClear
          onSearch={handleSearch}
          style={{ width: '100%' }}
        />
      </Col>

      {/* Date Filter Section */}
      <Col span={24}>
        <Divider orientation="left" style={{ margin: '16px 0 8px 0' }}>
          <CalendarOutlined /> กรองตามวันที่
        </Divider>

        {/* Quick Date Filters */}
        <Space wrap style={{ marginBottom: 12 }}>
          <Button
            size="small"
            type={selectedQuickFilter === 'all' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('all')}
          >
            ทั้งหมด
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'this_month' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('this_month')}
          >
            เดือนนี้
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'last_month' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('last_month')}
          >
            เดือนที่แล้ว
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'last_3_months' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('last_3_months')}
          >
            รอบ 3 เดือน
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'last_6_months' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('last_6_months')}
          >
            รอบ 6 เดือน
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'this_year' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('this_year')}
          >
            ปีนี้
          </Button>
          <Button
            size="small"
            type={selectedQuickFilter === 'last_year' ? 'primary' : 'default'}
            onClick={() => handleQuickDateFilter('last_year')}
          >
            ปีที่แล้ว
          </Button>
        </Space>

        {/* Custom Date Range Picker */}
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col span={24}>
            <RangePicker
              value={selectedDateRange}
              onChange={handleDateRangeChange}
              style={{ width: '100%', marginBottom: 8 }}
              placeholder={['วันเริ่มต้น', 'วันสิ้นสุด']}
              format="DD/MM/YYYY"
            />
          </Col>
          <Col span={12}>
            <Select
              placeholder="เลือกปี"
              style={{ width: '100%' }}
              onChange={handleYearFilter}
              value={selectedYear}
            >
              {yearOptions.map(y => (
                <Option key={y.value} value={y.value}>{y.label}</Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            <Select
              placeholder="เลือกเดือน"
              style={{ width: '100%' }}
              onChange={handleMonthFilter}
              value={selectedQuickFilter === 'month_select' ? selectedDateRange[0]?.month() : undefined}
              allowClear
              onClear={() => clearDateFilter()}
            >
              {monthOptions.map(m => (
                <Option key={m.value} value={m.value}>{m.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        {(selectedDateRange[0] || selectedDateRange[1]) && (
          <Button
            size="small"
            type="link"
            onClick={clearDateFilter}
            style={{ padding: 0 }}
          >
            ล้างตัวกรองวันที่
          </Button>
        )}
      </Col>

      <Col span={24}>
        <Select
          value={selectedCategory}
          onChange={handleCategoryFilter}
          style={{ width: '100%' }}
          placeholder="เลือกหมวดหมู่"
        >
          <Option value="all">ทุกหมวดหมู่</Option>
          {defaultCategories.map(category => (
            <Option key={category} value={category}>
              {category}
            </Option>
          ))}
        </Select>
      </Col>

      <Col span={24}>
        <Select
          value={selectedStatus}
          onChange={handleStatusFilter}
          style={{ width: '100%' }}
          placeholder="เลือกสถานะ"
        >
          <Option value="all">ทุกสถานะ</Option>
          <Option value="pending">รอดำเนินการ</Option>
          <Option value="approved">อนุมัติแล้ว</Option>
          <Option value="paid">จ่ายแล้ว</Option>
          <Option value="rejected">ไม่อนุมัติ</Option>
        </Select>
      </Col>

      <Col span={24}>
        <Select
          value={selectedSupplier}
          onChange={handleSupplierFilter}
          style={{ width: '100%' }}
          placeholder="เลือกผู้จำหน่าย"
          showSearch
          optionFilterProp="children"
        >
          <Option value="all">ทุกผู้จำหน่าย</Option>
          {suppliers.map(supplier => (
            <Option key={supplier.id} value={supplier.id}>
              {supplier.name} {supplier.company_name && `(${supplier.company_name})`}
            </Option>
          ))}
        </Select>
      </Col>

      {/* ✅ ปรับให้ ProjectFilter กว้างขึ้น */}
      <Col span={24}>
        <Select
          value={selectedProject}
          onChange={handleProjectFilter}
          style={{ width: '100%', maxWidth: 800 }}
          placeholder="เลือกโครงการ"
          showSearch
          optionFilterProp="children"
          loading={projectsLoading}
        >
          <Option value="all">ทุกโครงการ</Option>
          {projectOptions.map(project => (
            <Option key={project.value} value={project.value}>
              {project.label}
            </Option>
          ))}
        </Select>
      </Col>
    </Row>
  )

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      <Title level={2}>
        <MoneyCollectOutlined /> จัดการค่าใช้จ่าย
      </Title>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6} style={{ marginBottom: isMobile ? 16 : 0 }}>
          <Card>
            <Statistic
              title="ค่าใช้จ่ายรวม"
              value={stats.total_amount || 0}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="₿"
            />
          </Card>
        </Col>
        <Col xs={24} sm={6} style={{ marginBottom: isMobile ? 16 : 0 }}>
          <Card>
            <Statistic
              title="อนุมัติแล้ว"
              value={stats.approved_amount || 0}
              precision={2}
              prefix={<CheckOutlined />}
              suffix="₿"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6} style={{ marginBottom: isMobile ? 16 : 0 }}>
          <Card>
            <Statistic
              title="รอดำเนินการ"
              value={stats.pending_amount || 0}
              precision={2}
              prefix={<ExclamationCircleOutlined />}
              suffix="₿"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="จำนวนรายการ"
              value={stats.total_expenses || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Desktop View */}
      {!isMobile && (
        <>
          {/* Filters and Controls */}
          <Card style={{ marginBottom: 16 }}>
            {/* Date Filter Row */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Space wrap style={{ width: '100%' }}>
                  <Text strong>
                    <CalendarOutlined /> กรองตามช่วงเวลา:
                  </Text>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'all' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('all')}
                  >
                    ทั้งหมด
                  </Button>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'this_month' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('this_month')}
                  >
                    เดือนนี้
                  </Button>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'last_month' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('last_month')}
                  >
                    เดือนที่แล้ว
                  </Button>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'last_3_months' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('last_3_months')}
                  >
                    รอบ 3 เดือน
                  </Button>
                  <Button
                    size="small"
                    type={selectedQuickFilter === 'last_6_months' ? 'primary' : 'default'}
                    onClick={() => handleQuickDateFilter('last_6_months')}
                  >
                    รอบ 6 เดือน
                  </Button>
                  <RangePicker
                    value={selectedDateRange}
                    onChange={handleDateRangeChange}
                    placeholder={['วันเริ่มต้น', 'วันสิ้นสุด']}
                    format="DD/MM/YYYY"
                    style={{ width: 240 }}
                  />
                  <Select
                    placeholder="ปี"
                    style={{ width: 90 }}
                    onChange={handleYearFilter}
                    value={selectedYear}
                  >
                    {yearOptions.map(y => (
                      <Option key={y.value} value={y.value}>{y.label}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="เดือน"
                    style={{ width: 90 }}
                    onChange={handleMonthFilter}
                    value={selectedQuickFilter === 'month_select' ? selectedDateRange[0]?.month() : undefined}
                    allowClear
                    onClear={() => clearDateFilter()}
                  >
                    {monthOptions.map(m => (
                      <Option key={m.value} value={m.value}>{m.label}</Option>
                    ))}
                  </Select>
                  {(selectedDateRange[0] || selectedDateRange[1]) && (
                    <Button
                      size="small"
                      type="link"
                      onClick={clearDateFilter}
                      danger
                    >
                      ล้างตัวกรอง
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>

            {/* Other Filters Row */}
            <Row gutter={16} align="middle">
              <Col xs={24} sm={5}>
                <Search
                  placeholder="ค้นหาค่าใช้จ่าย..."
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={3}>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryFilter}
                  style={{ width: '100%' }}
                  placeholder="หมวดหมู่"
                >
                  <Option value="all">ทุกหมวดหมู่</Option>
                  {defaultCategories.map(category => (
                    <Option key={category} value={category}>
                      {category}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={3}>
                <Select
                  value={selectedStatus}
                  onChange={handleStatusFilter}
                  style={{ width: '100%' }}
                  placeholder="สถานะ"
                >
                  <Option value="all">ทุกสถานะ</Option>
                  <Option value="pending">รอดำเนินการ</Option>
                  <Option value="approved">อนุมัติแล้ว</Option>
                  <Option value="paid">จ่ายแล้ว</Option>
                  <Option value="rejected">ไม่อนุมัติ</Option>
                </Select>
              </Col>
              <Col xs={24} sm={4}>
                <Select
                  value={selectedSupplier}
                  onChange={handleSupplierFilter}
                  style={{ width: '100%' }}
                  placeholder="ผู้จำหน่าย"
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value="all">ทุกผู้จำหน่าย</Option>
                  {suppliers.map(supplier => (
                    <Option key={supplier.id} value={supplier.id}>
                      {supplier.name} {supplier.company_name && `(${supplier.company_name})`}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* ✅ ปรับให้ ProjectFilter กว้างขึ้น */}
              <Col span={4}>
                <Select
                  value={selectedProject}
                  onChange={handleProjectFilter}
                  style={{ width: '100%' }}
                  placeholder="เลือกโครงการ"
                  showSearch
                  optionFilterProp="children"
                  loading={projectsLoading}
                >
                  <Option value="all">ทุกโครงการ</Option>
                  {projectOptions.map(project => (
                    <Option key={project.value} value={project.value}>
                      {project.label}
                    </Option>
                  ))}
                </Select>
              </Col>

              {user.role === 'SUPER_ADMIN' && (
                <>
                  <Col xs={3} sm={3} style={{ textAlign: 'right', paddingLeft: 0 }}>
                    <Space>
                      <Button
                        icon={<PrinterOutlined />}
                        onClick={handlePrintTable}
                      >
                        พิมพ์
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddExpense}
                      >
                        เพิ่มค่าใช้จ่าย
                      </Button>
                    </Space>
                  </Col>
                </>
              )}

            </Row>
          </Card>

          {/* Expenses Table */}
          <Card>
            <Table
              columns={columns}
              dataSource={expenses}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1400 }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} จาก ${total} รายการ`
              }}
              footer={() => {
                const totalAmount = expenses.reduce((sum, expense) => sum + parseFloat(expense.total_amount || 0), 0)
                const totalTax = expenses.reduce((sum, expense) => sum + parseFloat(expense.tax_amount || 0), 0)

                return (
                  <div style={{
                    padding: '8px 16px',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 18
                  }}>
                    <div>
                      <Text strong>ยอดรวมทั้งสิ้น:</Text>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div><Text strong>จำนวนเงิน:</Text> {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      <div><Text strong>ภาษีซื้อ:</Text> {totalTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                )
              }}
            />
          </Card>
        </>
      )}

      {/* Mobile View */}
      {isMobile && (
        <>
          {/* Mobile Filter Bar */}
          {/* Mobile Filter Bar */}
          <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px' }}>
            <Row gutter={8} align="middle" wrap={false}>
              <Col flex="auto">
                <Search
                  placeholder="ค้นหา..."
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col flex="none">
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFiltersVisible(true)}
                />
              </Col>
            </Row>
          </Card>

          {/* Mobile Expense List */}
          <List
            loading={loading}
            dataSource={expenses}
            renderItem={(expense) => (
              <List.Item style={{ padding: 0 }}>
                <ExpenseCard expense={expense} />
              </List.Item>
            )}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              simple: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]}/${total}`
            }}
          />

          {/* Mobile Filters Drawer */}
          <Drawer
            title="ตัวกรอง"
            placement="bottom"
            onClose={() => setFiltersVisible(false)}
            open={filtersVisible}
            height="auto"
          >
            <FiltersComponent />
          </Drawer>

          {/* Float Button */}
          <FloatButton
            icon={<PlusOutlined />}
            type="primary"
            style={{ right: 24, bottom: 24 }}
            onClick={handleAddExpense}
          />
        </>
      )}

      {/* Add/Edit Expense Modal/Drawer */}
      {isMobile ? (
        <Drawer
          title={editingExpense ? 'แก้ไขค่าใช้จ่าย' : 'เพิ่มค่าใช้จ่ายใหม่'}
          placement="right"
          onClose={handleModalCancel}
          open={modalVisible}
          width="100%"
          destroyOnClose={true}
          extra={
            <Space>
              <Button onClick={handleModalCancel}>ยกเลิก</Button>
              <Button
                type="primary"
                onClick={handleModalOk}
                loading={uploading}
              >
                บันทึก
              </Button>
            </Space>
          }
        >
          <ExpenseForm />
        </Drawer>
      ) : (
        <Modal
          title={editingExpense ? 'แก้ไขค่าใช้จ่าย' : 'เพิ่มค่าใช้จ่ายใหม่'}
          open={modalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={800}
          okText="บันทึก"
          cancelText="ยกเลิก"
          confirmLoading={uploading}
          destroyOnClose={true}
        >
          <ExpenseForm />
        </Modal>
      )}

      {/* Expense Detail Modal/Drawer */}
      {isMobile ? (
        <Drawer
          title="รายละเอียดค่าใช้จ่าย"
          placement="right"
          onClose={handleModalCancel}
          open={detailVisible}
          width="100%"
        >
          <ExpenseDetail />
        </Drawer>
      ) : (
        <Modal
          title="รายละเอียดค่าใช้จ่าย"
          open={detailVisible}
          onCancel={handleModalCancel}
          width={700}
          footer={[
            <Button key="close" onClick={handleModalCancel}>
              ปิด
            </Button>
          ]}
        >
          <ExpenseDetail />
        </Modal>
      )}

      {/* PDF Preview Modal */}
      <Modal
        title="ตัวอย่างเอกสาร"
        open={pdfModalVisible}
        onCancel={() => setPdfModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 800}
        style={{ top: 20 }}
        bodyStyle={{ height: '80vh', padding: 0 }}
      >
        <iframe
          src={pdfDataUrl}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="PDF Preview"
        />
      </Modal>
    </div>
  );

  // Expense Form Component
  function ExpenseForm() {
    const [calcValues, setCalcValues] = useState({
      subtotal: 0,
      discount: 0,
      subtotalAfterDiscount: 0,
      taxAmount: 0,
      totalAmount: 0
    });

    const handleFormChange = async (changedValues) => {
      const items = form.getFieldValue('items') || [];
      const discount = form.getFieldValue('discount') || 0;
      const taxRate = form.getFieldValue('tax_rate') || 7;
      const currentVatMode = form.getFieldValue('vat_mode') || 'exclude';

      const calculations = calculateTotals(items, discount, taxRate, currentVatMode);
      setCalcValues(calculations);

      // Auto-generate expense number when date changes
      if (changedValues && changedValues.expense_date) {
        try {
          const formattedDate = changedValues.expense_date.format('YYYY-MM-DD');
          const newNumber = await expenseService.generateExpenseNumber(formattedDate);
          form.setFieldsValue({ expense_number: newNumber });
        } catch (error) {
          console.error('Error generating expense number:', error);
        }
      }
    };

    // Initial calculation
    useEffect(() => {
      handleFormChange();
    }, []);

    return (
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        initialValues={{
          expense_date: dayjs(),
          status: 'pending',
          tax_amount: 0,
          vat_mode: 'exclude',
          tax_rate: 7,
          discount: 0,
          items: [{ description: '', quantity: 1, unit_price: 0 }]
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="expense_number"
              label="เลขที่เอกสาร"
              rules={[{ required: true, message: 'กรุณาใส่เลขที่เอกสาร' }]}
            >
              <Input placeholder="เช่น EXP6810001" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="expense_date"
              label="วันที่"
              rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="category"
              label="หมวดหมู่"
              rules={[{ required: true, message: 'กรุณาเลือกหมวดหมู่' }]}
            >
              <Select placeholder="เลือกหมวดหมู่">
                {defaultCategories.map(category => (
                  <Option key={category} value={category}>
                    {category}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="รายละเอียด"
          rules={[{ required: true, message: 'กรุณาใส่รายละเอียด' }]}
        >
          <Input.TextArea rows={3} placeholder="รายละเอียดค่าใช้จ่าย..." />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="supplier_id"
              label="ผู้จำหน่าย"
            >
              <Select
                placeholder="เลือกผู้จำหน่าย"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {suppliers.map(supplier => (
                  <Option key={supplier.id} value={supplier.id}>
                    {supplier.name} {supplier.company_name && `(${supplier.company_name})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="payment_method"
              label="วิธีการชำระเงิน"
            >
              <Select placeholder="เลือกวิธีการชำระเงิน">
                {paymentMethods.map(method => (
                  <Option key={method.value} value={method.value}>
                    {method.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="project_id"
              label="โครงการ"
            >
              <Select
                placeholder="เลือกโครงการ"
                allowClear
                showSearch
                optionFilterProp="children"
                loading={projectsLoading}
              >
                {projectOptions.map(project => (
                  <Option key={project.value} value={project.value}>
                    {project.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider>รายการค่าใช้จ่าย</Divider>

        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row key={key} gutter={8} align="bottom">
                  <Col xs={24} sm={10}>
                    <Form.Item
                      {...restField}
                      name={[name, 'description']}
                      label={key === 0 ? 'รายละเอียด' : ''}
                      rules={[{ required: true, message: 'กรุณาใส่รายละเอียด' }]}
                    >
                      <Input placeholder="รายละเอียดรายการ" />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={4}>
                    <Form.Item
                      {...restField}
                      name={[name, 'quantity']}
                      label={key === 0 ? 'จำนวน' : ''}
                      rules={[{ required: true, message: 'จำนวน' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0.01}
                        placeholder="1"
                        precision={2}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'unit_price']}
                      label={key === 0 ? 'ราคาต่อหน่วย' : ''}
                      rules={[{ required: true, message: 'ราคา' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        placeholder="0.00"
                        precision={2}
                        addonAfter="₿"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={4}>
                    <Form.Item label={key === 0 ? 'ลบ' : ''}>
                      {fields.length > 1 && (
                        <Button
                          type="dashed"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
              >
                เพิ่มรายการ
              </Button>
            </>
          )}
        </Form.List>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="vat_mode"
              label="รูปแบบภาษี"
            >
              <Select>
                {vatModeOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="tax_rate"
              label="อัตราภาษี (%)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={0}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item
              name="discount"
              label="ส่วนลด"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\฿\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Summary Section */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <div style={{ width: '300px' }}>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col>รวมเป็นเงิน:</Col>
              <Col>฿{calcValues.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 8, color: 'red' }}>
              <Col>ส่วนลด:</Col>
              <Col>-฿{calcValues.discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Col>
            </Row>
            <Row justify="space-between" style={{ marginBottom: 8 }}>
              <Col>ภาษีมูลค่าเพิ่ม:</Col>
              <Col>฿{calcValues.taxAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
            <Row justify="space-between" style={{ fontWeight: 'bold', fontSize: '16px' }}>
              <Col>จำนวนเงินรวมทั้งสิ้น:</Col>
              <Col style={{ color: '#1890ff' }}>฿{calcValues.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Col>
            </Row>
          </div>
        </div>


        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="status"
              label="สถานะ"
            >
              <Select>
                <Option value="pending">รอดำเนินการ</Option>
                <Option value="approved">อนุมัติแล้ว</Option>
                <Option value="paid">จ่ายแล้ว</Option>
                <Option value="rejected">ไม่อนุมัติ</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label="หมายเหตุ"
        >
          <Input.TextArea rows={2} placeholder="หมายเหตุเพิ่มเติม..." />
        </Form.Item>

        <Form.Item label="แนบไฟล์เอกสาร">
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">คลิกหรือลากไฟล์มาวางที่นี่</p>
            <p className="ant-upload-hint">
              รองรับไฟล์ภาพ, PDF สำหรับใบเสร็จ (1 ไฟล์)
            </p>
          </Dragger>
          {uploading && (
            <Progress
              percent={100}
              status="active"
              style={{ marginTop: 8 }}
            />
          )}
        </Form.Item>
      </Form>
    )
  }

  // Expense Detail Component

  function ExpenseDetail() {
    if (!viewingExpense) return null

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Text strong>เลขที่: </Text>
            <Tag color="blue">{viewingExpense.expense_number}</Tag>
          </Col>
          <Col span={12}>
            <Text strong>วันที่: </Text>
            <Text>{dayjs(viewingExpense.expense_date).format('DD/MM/YYYY')}</Text>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Text strong>หมวดหมู่: </Text>
            <Tag color="green">{viewingExpense.category}</Tag>
          </Col>
          <Col span={12}>
            <Text strong>สถานะ: </Text>
            <Tag color={getStatusColor(viewingExpense.status)}>
              {getStatusText(viewingExpense.status)}
            </Tag>
          </Col>
        </Row>

        <div style={{ marginBottom: 16 }}>
          <Text strong>รายละเอียด: </Text>
          <p>{viewingExpense.description}</p>
        </div>

        {viewingExpense.supplier_name && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Text strong>ผู้จำหน่าย: </Text>
              <Text>{viewingExpense.supplier_name} {viewingExpense.supplier_company && `(${viewingExpense.supplier_company})`}</Text>
            </Col>
          </Row>
        )}

        {viewingExpense.project_name && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Text strong>โครงการ: </Text>
              <Text>{viewingExpense.project_name} {viewingExpense.project_code && `(${viewingExpense.project_code})`}</Text>
            </Col>
          </Row>
        )}

        {viewingExpense.items && viewingExpense.items.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>รายการค่าใช้จ่าย:</Text>
            <Table
              dataSource={viewingExpense.items}
              columns={[
                { title: 'รายละเอียด', dataIndex: 'description', key: 'description' },
                { title: 'จำนวน', dataIndex: 'quantity', key: 'quantity', width: 80 },
                { title: 'ราคาต่อหน่วย', dataIndex: 'unit_price', key: 'unit_price', width: 120, render: (price) => `₿${parseFloat(price).toFixed(2)}` },
                { title: 'รวม', dataIndex: 'total_price', key: 'total_price', width: 120, render: (price) => `₿${parseFloat(price).toFixed(2)}` },
              ]}
              pagination={false}
              size="small"
              style={{ marginTop: 8 }}
            />
          </div>
        )}

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ width: '300px' }}>
                <Row justify="space-between" style={{ marginBottom: 4 }}>
                  <Col><Text>รวมเป็นเงิน:</Text></Col>
                  <Col><Text>₿{parseFloat(viewingExpense.subtotal || viewingExpense.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Col>
                </Row>
                {viewingExpense.discount > 0 && (
                  <Row justify="space-between" style={{ marginBottom: 4 }}>
                    <Col><Text type="danger">ส่วนลด:</Text></Col>
                    <Col><Text type="danger">-₿{parseFloat(viewingExpense.discount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Col>
                  </Row>
                )}
                {viewingExpense.vat_mode !== 'no_tax' && (
                  <Row justify="space-between" style={{ marginBottom: 4 }}>
                    <Col><Text>ภาษีมูลค่าเพิ่ม ({viewingExpense.tax_rate || 7}%):</Text></Col>
                    <Col><Text>₿{parseFloat(viewingExpense.tax_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Col>
                  </Row>
                )}
                <Divider style={{ margin: '8px 0' }} />
                <Row justify="space-between">
                  <Col><Text strong>รวมทั้งสิ้น:</Text></Col>
                  <Col><Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                    ₿{parseFloat(viewingExpense.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text></Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>

        {viewingExpense.payment_method && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Text strong>วิธีการชำระเงิน: </Text>
              <Text>{paymentMethods.find(m => m.value === viewingExpense.payment_method)?.label || viewingExpense.payment_method}</Text>
            </Col>
          </Row>
        )}

        {viewingExpense.notes && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>หมายเหตุ: </Text>
            <p>{viewingExpense.notes}</p>
          </div>
        )}

        {viewingExpense.receipt_image && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>ใบเสร็จ/เอกสาร: </Text>
            <div style={{ marginTop: 8 }}>
              <FileViewer fileUrl={getFileUrl(viewingExpense.receipt_image)} />
            </div>
          </div>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Text strong>ผู้บันทึก: </Text>
            <Text>{viewingExpense.created_by_name}</Text>
          </Col>
          {viewingExpense.approved_by_name && (
            <Col span={12}>
              <Text strong>ผู้อนุมัติ: </Text>
              <Text>{viewingExpense.approved_by_name}</Text>
            </Col>
          )}
        </Row>
      </div>
    )
  }
}

export default ExpensesPage
